import {
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
    type ChangeEvent,
    type KeyboardEvent,
    type Ref,
} from 'react';
import './ChatAIMessageInput.scss';

export interface ChatAIMessageInputHandle {
    /** Фокус в поле, каретка — в конец текста. */
    focus: () => void;
    hasFocus: () => boolean;
}

interface ChatAIMessageInputProps {
    ref?: Ref<ChatAIMessageInputHandle>;
    /** Подпись поля для чтения с экрана. */
    label: string;
    placeholder?: string;
    /** Отправка недоступна: Enter переносит строку, кнопка выключена. Набирать текст можно. */
    sendDisabled?: boolean;
    /** Текст как набран, с переносами строк; поле после отправки очищается. */
    onSend: (text: string) => void;
    onChange?: (text: string) => void;
    /** Начальный текст. */
    defaultValue?: string;
}

/** Поле вопроса: Enter отправляет, Shift+Enter переносит строку; растёт по тексту до четырёх строк. */
export function ChatAIMessageInput({
    ref,
    label,
    placeholder,
    sendDisabled = false,
    onSend,
    onChange,
    defaultValue = '',
}: ChatAIMessageInputProps) {
    const fieldRef = useRef<HTMLTextAreaElement>(null);
    const [value, setValue] = useState(defaultValue);

    useImperativeHandle(
        ref,
        () => ({
            focus: () => {
                const field = fieldRef.current;
                if (!field) return;
                field.focus();
                field.setSelectionRange(field.value.length, field.value.length);
            },
            hasFocus: () => document.activeElement === fieldRef.current,
        }),
        [],
    );

    useLayoutEffect(() => {
        const field = fieldRef.current;
        if (!field) return;
        field.style.height = 'auto';
        field.style.height = `${field.scrollHeight}px`;
    }, [value]);

    const change = (text: string) => {
        setValue(text);
        onChange?.(text);
    };

    const send = () => {
        if (sendDisabled || value.length === 0) return;
        onSend(value);
        change('');
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        const composing = event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229;
        if (event.key !== 'Enter' || event.shiftKey || composing || sendDisabled) return;
        event.preventDefault();
        send();
    };

    return (
        <div className="chat-ai-message-input">
            <textarea
                ref={fieldRef}
                className="chat-ai-message-input__field"
                rows={1}
                aria-label={label}
                placeholder={placeholder}
                value={value}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => change(event.target.value)}
                onKeyDown={handleKeyDown}
            />
            <button
                type="button"
                className="chat-ai-message-input__send"
                aria-label="Отправить"
                disabled={sendDisabled}
                onClick={send}
            />
        </div>
    );
}
